import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { clsx } from 'clsx';

export function Pagination() {
  const { filteredCompanies, currentPage, perPage, setCurrentPage } = useAppStore();

  const totalItems = filteredCompanies.length;
  const totalPages = Math.ceil(totalItems / perPage);
  const startIdx = (currentPage - 1) * perPage + 1;
  const endIdx = Math.min(currentPage * perPage, totalItems);

  if (totalPages <= 1) return null;

  const maxVisible = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage = Math.min(totalPages, startPage + maxVisible - 1);

  if (endPage - startPage < maxVisible - 1) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  const pages: (number | 'ellipsis')[] = [];

  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) pages.push('ellipsis');
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) pages.push('ellipsis');
    pages.push(totalPages);
  }

  return (
    <div className="px-6 py-4 flex items-center justify-between">
      <div className="text-sm text-gray-400">
        Showing <span className="text-white">{startIdx}</span>-
        <span className="text-white">{endIdx}</span> of{' '}
        <span className="text-white">{totalItems}</span>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          className={clsx(
            'px-3 py-1.5 rounded text-sm flex items-center gap-1',
            'bg-gray-800 text-white',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'hover:bg-gray-700'
          )}
        >
          <ChevronLeft className="w-4 h-4" />
          Prev
        </button>

        {pages.map((page, idx) =>
          page === 'ellipsis' ? (
            <span key={`ellipsis-${idx}`} className="text-gray-500 px-2">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={clsx(
                'px-3 py-1.5 rounded text-sm',
                page === currentPage
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-white hover:bg-gray-700'
              )}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={clsx(
            'px-3 py-1.5 rounded text-sm flex items-center gap-1',
            'bg-gray-800 text-white',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'hover:bg-gray-700'
          )}
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
